import { ILocationIssueSummaryResponse } from '@kcftech/prometheustypescriptapiclient';
import { EmptyState } from 'components/empty_state/EmptyState';
import {
    ContentWrapper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableHeader,
    TableHeaderRow,
} from 'components/infinite_data_source_table/styled';
import { TableSortIcon } from 'components/infinite_data_source_table/TableSortIcon';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { CorporationIssuesTableContent } from 'pages/Corporation/issues/CorporationIssuesTableContent';
import {
    CenteredTableCell,
    CenteredTableRow,
    HeaderText,
} from 'pages/Corporation/issues/styled';
import { SortField, SortState } from 'pages/Corporation/issues/utils';
import { memo } from 'react';

type CorporationIssuesTableProps = {
    sort: SortState;
    onSort: (field: SortField) => void;
    data: ILocationIssueSummaryResponse[];
    isLoading: boolean;
    isFetching: boolean;
};

function CorporationIssuesTable({
    sort,
    onSort,
    data,
    isLoading,
    isFetching,
}: CorporationIssuesTableProps) {
    const showEmptyState = !isLoading && !isFetching && data.length === 0;
    const showLoadingState = isLoading || isFetching;

    return (
        <SpinnerArea isLoading={showLoadingState}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableHeaderRow>
                            <TableHeader
                                onClick={() => onSort('locationName')}
                                $sortable={true}
                                $isSorted={sort.field === 'locationName'}
                            >
                                <ContentWrapper>
                                    <HeaderText>Locations</HeaderText>
                                    <TableSortIcon
                                        isSorted={sort.field === 'locationName'}
                                        direction={sort.direction}
                                    />
                                </ContentWrapper>
                            </TableHeader>
                            <TableHeader
                                onClick={() => onSort('newIssueCount')}
                                $sortable={true}
                                $isSorted={sort.field === 'newIssueCount'}
                            >
                                <ContentWrapper>
                                    <HeaderText>New Issues</HeaderText>
                                    <TableSortIcon
                                        isSorted={sort.field === 'newIssueCount'}
                                        direction={sort.direction}
                                    />
                                </ContentWrapper>
                            </TableHeader>
                            <TableHeader
                                onClick={() => onSort('openIssueCount')}
                                $sortable={true}
                                $isSorted={sort.field === 'openIssueCount'}
                            >
                                <ContentWrapper>
                                    <HeaderText>Open Issues</HeaderText>
                                    <TableSortIcon
                                        isSorted={sort.field === 'openIssueCount'}
                                        direction={sort.direction}
                                    />
                                </ContentWrapper>
                            </TableHeader>
                            <TableHeader
                                onClick={() => onSort('averageDaysIssueOpened')}
                                $sortable={true}
                                $isSorted={sort.field === 'averageDaysIssueOpened'}
                            >
                                <ContentWrapper>
                                    <HeaderText>{`Average Days\nIssue Open`}</HeaderText>
                                    <TableSortIcon
                                        isSorted={sort.field === 'averageDaysIssueOpened'}
                                        direction={sort.direction}
                                    />
                                </ContentWrapper>
                            </TableHeader>
                            <TableHeader
                                onClick={() => onSort('downtimeHoursSaved')}
                                $sortable={true}
                                $isSorted={sort.field === 'downtimeHoursSaved'}
                            >
                                <ContentWrapper>
                                    <HeaderText>{`Downtime\nHours Saved`}</HeaderText>
                                    <TableSortIcon
                                        isSorted={sort.field === 'downtimeHoursSaved'}
                                        direction={sort.direction}
                                    />
                                </ContentWrapper>
                            </TableHeader>
                            <TableHeader
                                onClick={() => onSort('totalSavings')}
                                $sortable={true}
                                $isSorted={sort.field === 'totalSavings'}
                            >
                                <ContentWrapper>
                                    <HeaderText>Total Savings</HeaderText>
                                    <TableSortIcon
                                        isSorted={sort.field === 'totalSavings'}
                                        direction={sort.direction}
                                    />
                                </ContentWrapper>
                            </TableHeader>
                        </TableHeaderRow>
                    </TableHead>
                    <TableBody>
                        {showEmptyState ? (
                            <CenteredTableRow>
                                <CenteredTableCell colSpan={6}>
                                    <EmptyState message="No Issues found at this corporation" />
                                </CenteredTableCell>
                            </CenteredTableRow>
                        ) : (
                            <CorporationIssuesTableContent data={data} />
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </SpinnerArea>
    );
}

const memoCorporationIssuesTable = memo(CorporationIssuesTable);
export { memoCorporationIssuesTable as CorporationIssuesTable };
